import OrderPromoSummary from '../../promo/OrderPromoSummary';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const OrdersTabDesktop = ({ orders }) => {
  const [expandedOrders, setExpandedOrders] = useState({});

  const toggleDeliveryExpanded = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getShortDeliveryInfo = (order) => {
    if (!order.userInfo) return '';
    
    if (order.userInfo.receiveMethod && order.userInfo.receiveMethod.description) {
      return order.userInfo.receiveMethod.description;
    }
    
    return order.deliveryMethod || '';
  };

  const getFullDeliveryInfo = (order) => {
    if (!order.userInfo) return null;
    
    if (order.userInfo.receiveMethod && order.userInfo.receiveMethod.id === 3) {
      return null; // Для самовивізу повна інформація не потрібна
    }
    
    const details = [];
    if (order.userInfo.receiveMethod && order.userInfo.receiveMethod.description) {
      details.push(`Спосіб доставки: ${order.userInfo.receiveMethod.description}`);
    }
    if (order.userInfo.name && order.userInfo.surname) {
      details.push(`Отримувач: ${order.userInfo.name} ${order.userInfo.surname}`);
    }
    if (order.userInfo.phone) {
      details.push(`Телефон: ${order.userInfo.phone}`);
    }
    if (order.userInfo.city) {
      details.push(`Місто: ${order.userInfo.city}`);
    }
    if (order.userInfo.departmentNumber) {
      details.push(`Відділення: ${order.userInfo.departmentNumber}`);
    }
    if (order.userInfo.street) {
      details.push(`Вулиця: ${order.userInfo.street}`);
    }
    if (order.userInfo.buildingNumber) {
      details.push(`Будинок: ${order.userInfo.buildingNumber}`);
    }
    if (order.userInfo.flatNumber) {
      details.push(`Квартира: ${order.userInfo.flatNumber}`);
    }
    return details;
  };
  const formatCurrency = (amount) => {
    if (!amount) return "0грн";
    return `${amount.toLocaleString("uk-UA")}грн`;
  };

  const getOrderStatusColor = (orderStatus) => {
    // Мапінг статусів на кольори з таблиці
    const statusColorMap = {
      "Очікує на обробку": "#2F80ED", // NEW - Синій
      "Підтверджено, очікує на пакування": "#56CCF2", // WAITING_FOR_PACKAGING - Світло-синій
      "Упаковано, очікує на відправлення": "#2D9CDB", // WAITING_FOR_SENDING - Фіолетовий
      "Відправлено": "#27AE60", // SENT - Зелений
      "Передзамовлення": "#27AE60", // PREORDER - Зелений (як на сторінці товару, в кошику та підтвердженні)
      "Відкладено": "#F2C94C", // HOLD - Жовтий
      "Відмінено": "#EB5757", // CANCELLED - Червоний
      "Повернено": "#EB5757", // RETURNED — як скасування
    };
    return statusColorMap[orderStatus] || "#6C757D"; // Дефолтний сірий
  };

  const getOrderStatusIcon = (orderStatus) => {
    // Мапінг статусів на іконки
    const statusIconMap = {
      "Очікує на обробку": "bi-clock",
      "Підтверджено, очікує на пакування": "bi-check-circle",
      "Упаковано, очікує на відправлення": "bi-box-seam",
      "Відправлено": "bi-truck",
      "Передзамовлення": "bi-calendar-check",
      "Відкладено": "bi-pause-circle",
      "Відмінено": "bi-x-circle",
      "Повернено": "bi-arrow-counterclockwise",
    };
    return statusIconMap[orderStatus] || "bi-circle";
  };

  const getPaymentStatusColor = (paymentStatus) => {
    if (paymentStatus === "Оплачений" || paymentStatus === "Оплата при отриманні") {
      return "#27AE60"; // Зелений для успішних оплат
    }
    return "#E0A800"; // Менш агресивний жовтий для неоплачених
  };

  if (!orders || orders.length === 0) {
    return (
      <div className="text-center p-4">
        <p>Замовлень не знайдено</p>
      </div>
    );
  }

  return (
    <div className="row">
      {orders.map((order) => (
        <div key={order.orderId} className="col-12 mb-3">
          <div className="card">
            <div className="card-body">
              {/* Перший рядок: Номер замовлення і статус */}
              <div className="row mb-2">
                <div className="col-12 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <strong className="mr-2">{order.orderId}</strong>
                    <strong style={{ color: getOrderStatusColor(order.orderStatus), fontSize: '0.95rem' }}>
                      <i className={`bi ${getOrderStatusIcon(order.orderStatus)} mr-1`}></i>
                      {order.orderStatus}
                    </strong>
                  </div>
                  <small className="text-muted">{order.createTime}</small>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12" style={{ borderBottom: '1px solid #e9ecef' }}></div>
              </div>

              {/* Третій рядок: Решта інформації */}
              <div className="row align-items-center mb-2">
                <div className="col-3">
                  <div className="d-flex flex-column">
                    <small className="text-muted">Сума</small>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {order.totalPrice !== order.discountedPrice ? (
                        <>
                          <span className="text-decoration-line-through text-muted mr-2">
                            {formatCurrency(order.totalPrice)}
                          </span>
                          <strong>{formatCurrency(order.discountedPrice)}</strong>
                        </>
                      ) : (
                        <strong>{formatCurrency(order.discountedPrice)}</strong>
                      )}
                    </div>
                    <OrderPromoSummary order={order} />
                  </div>
                </div>
                <div className="col-3" style={{ borderLeft: '1px solid #e9ecef', paddingLeft: '1rem' }}>
                  <div className="d-flex flex-column">
                    <small className="text-muted">Оплата</small>
                    <div>
                      <span>{order.paymentType}</span>
                      {order.paymentType !== "Оплата при отриманні" && (
                        <span className="ml-2">
                          {order.paymentStatus === "Оплачений" ? (
                            <strong style={{ color: getPaymentStatusColor(order.paymentStatus), fontSize: "0.875rem" }}>
                              <i className="bi bi-check-circle-fill mr-1" style={{ color: "#27AE60", fontSize: "0.875rem" }}></i>
                              {order.paymentStatus}
                            </strong>
                          ) : (
                            <strong style={{ color: getPaymentStatusColor(order.paymentStatus), fontSize: "0.875rem" }}>
                              <i className="bi bi-clock mr-1" style={{ color: "#E0A800", fontSize: "0.875rem" }}></i>
                              {order.paymentStatus}
                            </strong>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {order.userInfo && (
                  <div className="col-4" style={{ borderLeft: '1px solid #e9ecef', paddingLeft: '1rem' }}>
                    <div className="d-flex flex-column">
                      <small className="text-muted">Доставка</small>
                      {expandedOrders[order.orderId] ? (
                        <div>
                          <div className="d-flex align-items-start mb-2">
                            <div className="flex-grow-1 text-muted" style={{ fontSize: '0.875rem' }}>
                              {getFullDeliveryInfo(order) && getFullDeliveryInfo(order).map((detail, idx) => (
                                <div key={idx}>{detail}</div>
                              ))}
                            </div>
                            {getFullDeliveryInfo(order) && getFullDeliveryInfo(order).length > 0 && (
                              <i 
                                className="bi bi-chevron-up ml-2"
                                style={{ cursor: 'pointer', fontSize: '0.75rem', flexShrink: 0, marginTop: '2px' }}
                                onClick={() => toggleDeliveryExpanded(order.orderId)}
                              ></i>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center">
                          <span className={order.userInfo.receiveMethod && order.userInfo.receiveMethod.id === 3 ? "" : "text-truncate"}>
                            {getShortDeliveryInfo(order)}
                          </span>
                          {getFullDeliveryInfo(order) && getFullDeliveryInfo(order).length > 0 && (
                            <i 
                              className="bi bi-chevron-down ml-2"
                              style={{ cursor: 'pointer', fontSize: '0.75rem', flexShrink: 0 }}
                              onClick={() => toggleDeliveryExpanded(order.orderId)}
                            ></i>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {order.trackingNumber && (
                  <div className={order.userInfo ? "col-2" : "col-5"} style={{ borderLeft: '1px solid #e9ecef', paddingLeft: '1rem' }}>
                    <div className="d-flex flex-column">
                      <small className="text-muted">
                        <i className="bi bi-upc mr-1"></i>
                        Трекінг-номер
                      </small>
                      <div
                        onClick={() => window.open(`https://novaposhta.ua/tracking/${order.trackingNumber}`, '_blank')}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}
                      >
                        <strong style={{ color: '#000' }}>
                          {order.trackingNumber}
                        </strong>
                        <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.875rem', color: '#000' }}></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Інформація про товари та кнопка Детальніше */}
              <div className="row mt-3 pt-2 border-top">
                <div className="col-12 d-flex justify-content-between align-items-center">
                  {(order.distinctProductsCount || order.totalQuantity) && (
                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Всього:{' '}
                      {order.distinctProductsCount && (
                        <span>{order.distinctProductsCount} {order.distinctProductsCount === 1 ? 'найменування' : 'найменувань'}</span>
                      )}
                      {order.distinctProductsCount && order.totalQuantity && <span className="mx-2">•</span>}
                      {order.totalQuantity && (
                        <span>{order.totalQuantity} {order.totalQuantity === 1 ? 'шт' : 'шт'}</span>
                      )}
                    </div>
                  )}
                  <Link 
                    to={`/user/order/${order.orderId}`}
                    className="btn btn-link p-0" 
                    style={{ color: '#000', textDecoration: 'none' }}
                  >
                    <strong>Детальніше</strong>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OrdersTabDesktop;
